import type WaDataGrid from "@web.awesome.me/webawesome-pro/dist/components/data-grid/data-grid.js";
import type WaDialog from "@web.awesome.me/webawesome-pro/dist/components/dialog/dialog.js";
import htmx from "htmx.org";
import { html } from "lit";
import { z } from "zod";
import { checkNotNil } from "../../check-not-nil.js";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper.js";
import { stateProperty } from "../../property.js";
import { Datetime, DatetimeString } from "../../schemas/datetime.js";
import {
  type ParserSchema,
  type ParserType,
  parser,
} from "../../schemas/parser.js";
import { Task } from "../../schemas/task.js";
import { gridSearch } from "../../search.js";
import { queryPage } from "../../utils.js";
import { WmElement } from "../element.js";
import type { WmDeletionRequest } from "./details.js";

const TaskDoneToday = parser(
  z.strictObject({
    ...Task.schema.shape,
    // Forcing the presence of done at.
    doneAt: DatetimeString.schema,
  }),
);

type Tasks = ParserType<typeof Tasks>;
const Tasks = stateProperty(z.array(TaskDoneToday.schema), {
  defaultValue: [],
});

class WmTasksGrid extends WmElement {
  static readonly TAG_NAME = "wm-tasks-done-today-grid";

  static properties = {
    tasks: Tasks,
    isMobile: { attribute: false },
  };

  declare tasks: Tasks;
  declare isMobile: boolean;
  #pageObserver?: MutationObserver;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof Tasks>>;

  constructor() {
    super();
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: Tasks.schema,
      callback: (v) => (this.tasks = v),
      logger: this.logger,
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializePageView();
  }

  disconnectedCallback() {
    this.#pageObserver?.disconnect();
    this.#pageObserver = undefined;
    super.disconnectedCallback();
  }

  protected override render() {
    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      ${this.#renderGrid()}`;
  }

  // TODO: deletion dialog component.
  #renderGrid() {
    return html`
      <wa-dialog label="Are you sure?" id="deletion-dialog">
        This action is irreversible.
        <div slot="footer" class="wa-cluster">
          <wa-button variant="brand" data-dialog="close">Cancel</wa-button>
          <wa-button id="deletion-confirmation" variant="danger" @click=${this.#confirmDeletion}>Confirm</wa-button>
        </div>
      </wa-dialog>
      <wa-data-grid
        label="Tasks done today"
        page-size="10"
        paginage
        resizable
        row-key="id"
        size="s"
        striped
        with-search>
        <span slot="empty">You haven't done any tasks yet. Pathetic.</span>
      </wa-data-grid>
      `;
  }

  protected updated(): void {
    this.#handleUpdated();
  }

  #confirmDeletion() {
    const dialog = this.#queryDialog();
    const taskId = dialog.dataset.taskId;
    htmx.ajax("DELETE", `/app/tasks/${taskId}`);
  }

  #descriptionClickHandler(id: string) {
    return (e: PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      htmx.ajax("GET", `/app/tasks/${id}`, {
        target: "body",
        push: "true",
      });
    };
  }

  async #handleUpdated() {
    const dataGrid = this.#queryGrid();
    await dataGrid.updateComplete;
    dataGrid.searchFn = gridSearch;
    const dateFormatter = (v: unknown) => {
      if (v == null) {
        return "";
      }
      return Datetime.formatPretty(Datetime.codec.parse(v));
    };
    dataGrid.columns = [
      {
        field: "description",
        label: "Description",
        formatter: (v, row) =>
          html`<a href="/app/tasks/${row.id}" @click=${this.#descriptionClickHandler(row.id as string)}>${v}</a>`,
      },
      {
        field: "createdAt",
        label: "Created At",
        hidden: this.isMobile,
        formatter: dateFormatter,
      },
      {
        field: "doneAt",
        label: "Done At",
        hidden: this.isMobile,
        formatter: dateFormatter,
      },
      {
        field: "tags",
        label: "Tags",
        hidden: this.isMobile,
        formatter: (v) => html`<wm-tag-cell .tags=${v}></wm-tag-cell>`,
      },
      {
        id: "actions",
        label: "Actions",
        hidden: this.isMobile,
        formatter: (_, row) => html`
          <wa-button size="xs" appearance="plain" @click=${() => htmx.ajax("POST", `/app/tasks/${row.id}/undone`)}>
            <wa-icon name="arrow-rotate-left" style="color: var(--wa-color-success)"></wa-icon>
          </wa-button>
          <wa-button size="xs" appearance="plain" @click=${() => this.#promptDeletionConfirmation(row.id as string)}>
            <wa-icon name="trash" style="color: var(--wa-color-danger)"></wa-icon>
          </wa-button>
        `,
      },
    ];
    dataGrid.rowDetail = (row) =>
      html`<wm-task-details .details=${row} @wm-deletion-request=${(e: WmDeletionRequest) => this.#promptDeletionConfirmation(e.id)}></wm-task-details>`;
    dataGrid.data = this.tasks;
  }

  #initializePageView() {
    // At this time, we actually consider it an error if no wraping wa-page
    // can be found.
    const page = queryPage();
    this.isMobile = page.view === "mobile";
    this.#pageObserver = new MutationObserver(() => {
      this.isMobile = page.view === "mobile";
    });
    this.#pageObserver.observe(page, {
      attributes: true,
      attributeFilter: ["view"],
    });
  }

  #promptDeletionConfirmation(taskId: string) {
    const dialog = this.#queryDialog();
    dialog.dataset.taskId = taskId;
    dialog.open = true;
  }

  #queryDialog(): WaDialog {
    return checkNotNil(this.renderRoot.querySelector("wa-dialog"));
  }

  #queryGrid(): WaDataGrid {
    return checkNotNil(this.renderRoot.querySelector("wa-data-grid"));
  }
}

customElements.define(WmTasksGrid.TAG_NAME, WmTasksGrid);

declare global {
  interface HTMLElementTagNameMap {
    [WmTasksGrid.TAG_NAME]: WmTasksGrid;
  }
}
